import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from "@shadcn/components/ui/card";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@shadcn/components/ui/table";
import { Button } from "@shadcn/components/ui/button";
import { Download, ChevronDown, ChevronRight } from 'lucide-react';
import { Bar } from 'react-chartjs-2';
import { format } from "date-fns";
import Papa from 'papaparse';

const ByOrderSourceTab = ({ 
  data, 
  barData, 
  loading, 
  filters,
  orderSourceOptionsMap
}) => {
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleExpand = (orderSource) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(orderSource)) {
      newExpanded.delete(orderSource);
    } else {
      newExpanded.add(orderSource);
    }
    setExpandedRows(newExpanded);
  };

  const exportToCSV = () => {
    if (!data?.tableData) return;

    const csvData = [];
    
    // Add header row
    csvData.push({
      'Order Source': 'Order Source Summary',
      'Quantity': '',
      'Percentage': '',
      'SKU': '',
      'Product Name': '',
      'Category': '',
      'Sales Units': '',
      'SOH Units': ''
    });

    data.tableData.forEach(row => {
      // Add main row
      csvData.push({
        'Order Source': orderSourceOptionsMap?.get(row?.orderSource) || row?.orderSource || 'Unknown',
        'Quantity': row?.quantity || 0,
        'Percentage': data.total > 0 ? ((row?.quantity / data.total) * 100).toFixed(1) + '%' : '0%',
        'SKU': '',
        'Product Name': '',
        'Category': '',
        'Sales Units': '',
        'SOH Units': ''
      });

      // Add detail rows if available
      if (row?.details && row.details.length > 0) {
        row.details.forEach(detail => {
          csvData.push({
            'Order Source': '',
            'Quantity': '',
            'Percentage': '',
            'SKU': detail.sku || detail.skus?.join(', ') || 'N/A',
            'Product Name': detail.productName || detail.products?.join(', ') || 'N/A',
            'Category': detail.category || 'N/A',
            'Sales Units': detail.salesUnits || 0,
            'SOH Units': detail.sohUnits || 0
          });
        });
      }
    });

    // Add summary row
    csvData.push({
      'Order Source': 'Total',
      'Quantity': data.total || 0,
      'Percentage': '100%',
      'SKU': '',
      'Product Name': '',
      'Category': '',
      'Sales Units': '',
      'SOH Units': ''
    });

    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    
    link.setAttribute("href", url);
    link.setAttribute("download", `order-source-report-${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center h-32">
          <div className="text-gray-500">Loading...</div>
        </div>
      );
    }

    if (!data?.tableData || data.tableData.length === 0) {
      return (
        <div className="flex justify-center items-center h-32">
          <div className="text-gray-500">No data available</div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {barData && (
          <div>
            <h3 className="text-lg font-medium mb-2">Order Source Distribution</h3>
            <div className="grid grid-cols-1 gap-4">
              <div className="h-[400px] flex items-center justify-center">
                <Bar 
                  data={barData} 
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    indexAxis: 'x',
                    scales: {
                      x: {
                        title: {
                          display: true,
                          text: 'Quantity'
                        }
                      },
                      y: {
                        title: {
                          display: true,
                          text: 'Order Source'
                        }
                      }
                    },
                    plugins: {
                      legend: {
                        display: false
                      },
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            const value = context.raw;
                            const percentage = ((value / data.total) * 100).toFixed(1);
                            return `Quantity: ${value} (${percentage}%)`;
                          }
                        }
                      }
                    }
                  }}
                />
              </div>
              <div>
                <Table className="w-full">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Order Source</TableHead>
                      <TableHead className="text-right">Quantity</TableHead>
                      <TableHead className="text-right">Percentage</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.tableData.map((row, index) => (
                      <React.Fragment key={`row-${index}`}>
                        <TableRow className="hover:bg-gray-50">
                          <TableCell className="p-2">
                            {row?.details && row.details.length > 0 ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => toggleExpand(row.orderSource)}
                                className="p-1 h-6 w-6"
                              >
                                {expandedRows.has(row.orderSource) ? 
                                  <ChevronDown className="h-4 w-4" /> : 
                                  <ChevronRight className="h-4 w-4" />
                                }
                              </Button>
                            ) : null}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            {orderSourceOptionsMap?.get(row?.orderSource) || row?.orderSource || 'Unknown'}
                          </TableCell>
                          <TableCell className="text-right">{row?.quantity || 0}</TableCell>
                          <TableCell className="text-right">
                            {data.total > 0 ? ((row?.quantity / data.total) * 100).toFixed(1) : 0}%
                          </TableCell>
                        </TableRow>
                        {expandedRows.has(row.orderSource) && row?.details && row.details.length > 0 && (
                          <TableRow>
                            <TableCell colSpan={4} className="p-0">
                              <div className="bg-gray-50 p-4 border-t">
                                <Table className="w-full text-sm">
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead className="text-xs">SKU</TableHead>
                                      <TableHead className="text-xs">Product Name</TableHead>
                                      <TableHead className="text-xs">Category</TableHead>
                                      <TableHead className="text-xs text-right">Sales Units</TableHead>
                                      <TableHead className="text-xs text-right">SOH Units</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {row.details.map((detail, detailIndex) => (
                                      <TableRow key={`detail-${index}-${detailIndex}`} className="text-xs">
                                        <TableCell className="font-mono text-xs">
                                          {detail.sku || detail.skus?.join(', ') || 'N/A'}
                                        </TableCell>
                                        <TableCell className="text-xs">
                                          {detail.productName || detail.products?.join(', ') || 'N/A'}
                                        </TableCell>
                                        <TableCell className="text-xs">
                                          {detail.category || 'N/A'}
                                        </TableCell>
                                        <TableCell className="text-right text-xs">
                                          {detail.salesUnits || 0}
                                        </TableCell>
                                        <TableCell className="text-right text-xs">
                                          {detail.sohUnits || 0}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                   
                    <TableRow className="bg-gray-50">
                      <TableCell></TableCell>
                      <TableCell className="font-bold">Total</TableCell>
                      <TableCell className="text-right font-bold">{data.total || 0}</TableCell>
                      <TableCell className="text-right font-bold">100%</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>
            Sale state by Order Source
            <span className="ml-2 text-sm font-normal">
              ({filters?.startDate && filters?.endDate ? 
                `${format(filters.startDate, "dd/MM/yyyy")} to ${format(filters.endDate, "dd/MM/yyyy")}` : 
                'No date range selected'
              })
            </span>
          </CardTitle>
          <Button
            onClick={exportToCSV}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
            disabled={!data?.tableData || data.tableData.length === 0}
          >
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {renderContent()}
      </CardContent>
    </Card>
  );
};

export default ByOrderSourceTab; 
